import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";
import styles from "./Select.module.css";

export interface Option {
  value: string;
  label: string;
  /** Render this option's label in monospace — for ID-like values. */
  mono?: boolean;
  /** Small muted note pinned to the option's right edge, e.g. a field type. */
  meta?: string;
  /**
   * Render the label muted (in the list *and* in the trigger when selected) —
   * for "no constraint" entries such as "All tiers", which read as an absent
   * value rather than a picked one.
   */
  muted?: boolean;
  /** Draw a hairline under this option — separates it from the group below. */
  separatorAfter?: boolean;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /**
   * Render the selected value in a monospace font (12.5px) — for ID-like
   * values such as asset IDs. Defaults to false (body font).
   */
  monospaceValue?: boolean;
  /**
   * Extra class on the root wrapper — lets a host override the default
   * `min-width: 180px` (e.g. compact selects inside a grid column).
   */
  className?: string;
  /** Accessible name for the trigger, when no visible <label> points at it. */
  "aria-label"?: string;
  /**
   * Draws a × inside the trigger that resets the choice — for a filter whose
   * value is shown in the trigger itself rather than in a chip of its own.
   */
  onClear?: () => void;
  /** Accessible name of the × — required with `onClear`. */
  clearLabel?: string;
}

interface MenuPosition {
  top: number;
  left: number;
  width: number;
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  id,
  monospaceValue = false,
  className,
  "aria-label": ariaLabel,
  onClear,
  clearLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const selected = options.find((o) => o.value === value);

  // Position the portal-rendered listbox under the trigger.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [open]);

  // Reposition on viewport changes while open.
  useEffect(() => {
    if (!open) return;
    function update() {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // Close on outside click (covers both trigger and portal-rendered listbox).
  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (listboxRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  function onKey(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (open) {
        onChange(options[activeIdx].value);
        setOpen(false);
      } else {
        setOpen(true);
        setActiveIdx(Math.max(0, options.findIndex((o) => o.value === value)));
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(options.length - 1, i + 1));
      if (!open) setOpen(true);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    }
  }

  return (
    <div className={`${styles.root} ${className ?? ""}`}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""} ${
          onClear ? styles.triggerClearable : ""
        }`}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKey}
      >
        {selected ? (
          <span
            className={`${styles.value} ${monospaceValue ? styles.valueMono : ""} ${
              selected.muted ? styles.valueMuted : ""
            }`.trim()}
          >
            {selected.label}
          </span>
        ) : (
          <span className={styles.placeholder}>{placeholder ?? "Select…"}</span>
        )}
        <ChevronDown size={16} aria-hidden className={open ? styles.chevronOpen : styles.chevron} />
      </button>
      {onClear && (
        // A sibling laid over the trigger rather than a child of it: a button
        // inside a button is neither valid nor reachable from the keyboard.
        <button
          type="button"
          aria-label={clearLabel}
          className={styles.clear}
          disabled={disabled}
          onClick={() => {
            setOpen(false);
            onClear();
          }}
        >
          <X size={12} aria-hidden="true" />
        </button>
      )}
      {open && pos &&
        createPortal(
          <ul
            ref={listboxRef}
            role="listbox"
            className={styles.listbox}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
            }}
          >
            {options.map((o, i) => (
              <Fragment key={o.value}>
                <li
                  role="option"
                  aria-selected={o.value === value}
                  className={`${styles.option} ${i === activeIdx ? styles.active : ""}`}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <span
                    className={`${o.mono ? styles.optionMono : ""} ${
                      o.muted ? styles.valueMuted : ""
                    }`.trim() || undefined}
                  >
                    {o.label}
                  </span>
                  {o.meta && <span className={styles.optionMeta}>{o.meta}</span>}
                </li>
                {o.separatorAfter && (
                  <li role="presentation" className={styles.optionSep} />
                )}
              </Fragment>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
